import { useMediaQuery } from "react-responsive";
import DotsBackground from "../components/DotsBackground.tsx";
import LeftSidebar from "../components/LeftSidebar.tsx";
import RightSidebar from "../components/RightSidebar.tsx";
import BottomNav from "../components/BottomNav.tsx";
import TopProfileBar from "../components/TopProfileBar.tsx";
import AchievementBox from "../components/achievement/AchievementBox.tsx";
import { achievementList } from "../data/achievement/achievementData.ts";
import { getUserData } from "../utils/userStorage.ts";
import { useState, useEffect } from "react";
import { Instagram, Linkedin } from "lucide-react";

const AchievementPage = () => {
  const [currentSection, setCurrentSection] = useState("Pencapaian");
  const [darkMode, setDarkMode] = useState(() => {
    const data = getUserData();
    return data?.meta?.darkMode ?? false;
  });

  // Responsive breakpoints
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 1023 });
  const isDesktop = useMediaQuery({ minWidth: 1024 });
  const isLandscape = useMediaQuery({ orientation: "landscape" });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const handleStorage = (e: StorageEvent) => {
      if (e.key === "galaxy_aksara_user_data") {
        const data = getUserData();
        setDarkMode(data?.meta?.darkMode ?? false);
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  return (
    <div
      className={`${
        darkMode ? "bg-gray-900" : "bg-gray-100"
      } flex min-h-screen relative`}
    >
      <DotsBackground darkMode={darkMode} />
      {/* Left Sidebar: desktop shows full, tablet shows compact icon-only, mobile hides */}
      {isDesktop && (
        <LeftSidebar
          currentSection={currentSection}
          setCurrentSection={setCurrentSection}
          darkMode={darkMode}
        />
      )}
      {isTablet && !isMobile && (
        <LeftSidebar
          currentSection={currentSection}
          setCurrentSection={setCurrentSection}
          darkMode={darkMode}
          compact
        />
      )}
      <div className="flex-1 flex">
        {/* Top Profile Bar (mobile/tablet only) */}
        {isMobile && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              zIndex: 30,
            }}
          >
            <TopProfileBar darkMode={darkMode}>
              {/* Only show profile section from RightSidebar */}
              <RightSidebar darkMode={darkMode} profileOnly />
            </TopProfileBar>
          </div>
        )}
        <main
          className="flex-1 relative z-10 bg-transparent pt-0 md:pt-0"
          style={isMobile ? { paddingTop: 64, paddingBottom: 64 } : {}}
        >
          <div className="max-w-3xl mx-auto py-10 px-4">
            <h1
              className={`text-4xl text-center font-bold mb-6 ${
                darkMode ? "text-gray-100" : "text-gray-800"
              }`}
              style={{ fontFamily: "Feather Bold" }}
            >
              Daftar Pencapaian
            </h1>

            <div className="space-y-8">
              {achievementList.map((stages, idx) => (
                <AchievementBox
                  key={idx}
                  stages={stages}
                  initialStage={0}
                  initialProgress={0}
                  darkMode={darkMode}
                />
              ))}
            </div>
          </div>
          {(isMobile) && (
            <footer className="w-full pt-2 md:pt-2 pb-12 md:pb-10 bg-transparent flex flex-col items-center gap-2 z-10 relative">
              <div
                className="text-gray-500 text-sm font-semibold"
                style={{ fontFamily: "DIN Next Rounded" }}
              >
                &copy; 2025 Galaxy Developer
              </div>
              <div className="flex gap-4 mt-1">
                <a
                  href="https://www.instagram.com/galaxy.developer/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-gray-500 hover:text-pink-500 transition font-semibold text-sm"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a
                  href="https://www.linkedin.com/company/galaxydeveloper"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-gray-500 hover:text-blue-700 transition font-semibold text-sm"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
            </footer>
          )}
        </main>
        {(isDesktop || isTablet) && <RightSidebar darkMode={darkMode} />}
      </div>
      {/* Bottom Nav (mobile only) */}
      {isMobile && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            width: "100vw",
            zIndex: 30,
          }}
        >
          <BottomNav
            currentSection={currentSection}
            setCurrentSection={setCurrentSection}
            darkMode={darkMode}
          />
        </div>
      )}
    </div>
  );
};

export default AchievementPage;
